/**
 * 2D landing-page session history. Reads the locally-stored (and cloud-synced)
 * session log and surfaces it in two layers:
 *   - a compact, read-only panel on the landing: trends + the last 5 sessions as
 *     flat rows (date · type · headline stat) + a "Manage sessions" button;
 *   - a full management modal (<dialog>): the complete list with expandable
 *     detail, per-session and bulk JSON export, and deletion (single + all).
 *
 * Pure DOM (no canvas / no VR). Deletion must also reach Firestore — the cloud
 * sync is union-only and would re-import a locally-deleted session on next login.
 * To keep this eager module free of a static Firebase import (the SDK is lazy),
 * the cloud delete is pulled via dynamic import only when Firebase is configured.
 *
 * Stat labels mirror the VR summary panel; d'arte terms reuse strokeLabels.
 */
import { difficultyLabel, t, getLocale } from '../i18n';
import {
  clearAllSessions,
  deleteSession,
  exportAllSessionsJSON,
  exportSessionJSON,
  listSessions,
  subscribeSessionsChanged,
  type StoredSession,
} from '../storage/sessions';
import { isFirebaseConfigured } from '../firebase/config';
import type { DrillReasonKey, DrillStats, PellStats, SessionStats } from './stats';
import { drillTargetName, zoneLabel } from './strokeLabels';
import { formatClock } from './timer';
import { ZONE, type Difficulty, type Zone } from '../config/constants';

const COMPACT_LIMIT = 5;

let bodyEl: HTMLElement | null = null;

/** Wire the landing history panel. Safe no-op if the container is absent. */
export function bootstrapHistory(): void {
  const container = document.getElementById('history');
  if (!container) return;

  const panel = document.createElement('details');
  panel.className = 'history-panel';
  const summary = document.createElement('summary');
  summary.textContent = t('historyTitle');
  panel.appendChild(summary);

  bodyEl = document.createElement('div');
  bodyEl.className = 'history-body';
  panel.appendChild(bodyEl);
  container.appendChild(panel);

  render();
  // Repaint on any change to the stored set — a new local save, a cloud import,
  // or a deletion made from the management modal.
  subscribeSessionsChanged(render);
}

// ── Compact landing panel ────────────────────────────────────
function render(): void {
  if (!bodyEl) return;
  bodyEl.replaceChildren();

  const sessions = listSessions(); // newest first
  if (sessions.length === 0) {
    const empty = document.createElement('p');
    empty.className = 'history-empty';
    empty.textContent = t('historyEmpty');
    bodyEl.appendChild(empty);
    return;
  }

  const trends = buildTrends(sessions);
  if (trends) bodyEl.appendChild(trends);

  const list = document.createElement('div');
  list.className = 'history-rows';
  for (const s of sessions.slice(0, COMPACT_LIMIT)) {
    // Guard per session: a single malformed/legacy record must not abort the panel.
    try {
      list.appendChild(buildCompactRow(s));
    } catch (err) {
      console.warn('history: skipping unrenderable session', s.id, err);
    }
  }
  bodyEl.appendChild(list);

  const manage = document.createElement('button');
  manage.type = 'button';
  manage.className = 'history-manage';
  manage.textContent = t('historyManage');
  manage.addEventListener('click', openManageDialog);
  bodyEl.appendChild(manage);
}

/** A read-only summary row (date · type · headline). Not expandable. */
function buildCompactRow(s: StoredSession): HTMLElement {
  const row = document.createElement('div');
  row.className = 'history-row';
  const date = document.createElement('span');
  date.className = 'hi-date';
  date.textContent = formatDate(s.startedAt);
  const type = document.createElement('span');
  type.className = 'hi-type';
  type.textContent = typeLabel(s.stats);
  const headline = document.createElement('span');
  headline.className = 'hi-headline';
  headline.textContent = headlineStat(s.stats);
  row.append(date, type, headline);
  return row;
}

// ── Management modal ─────────────────────────────────────────
function openManageDialog(): void {
  const dialog = document.createElement('dialog');
  dialog.className = 'history-dialog';

  const header = document.createElement('div');
  header.className = 'history-dialog-header';
  const title = document.createElement('h2');
  title.className = 'history-dialog-title';
  title.textContent = t('historyManageTitle');
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'history-dialog-close';
  closeBtn.textContent = t('settingsClose');
  closeBtn.addEventListener('click', () => dialog.close());
  header.append(title, closeBtn);

  const tools = document.createElement('div');
  tools.className = 'history-dialog-tools';
  const exportAll = document.createElement('button');
  exportAll.type = 'button';
  exportAll.className = 'history-export';
  exportAll.textContent = t('historyExportAll');
  exportAll.addEventListener('click', () => download('sessions.json', exportAllSessionsJSON()));
  const deleteAll = document.createElement('button');
  deleteAll.type = 'button';
  deleteAll.className = 'history-del-btn danger';
  deleteAll.textContent = t('historyDeleteAll');
  tools.append(exportAll, deleteAll);

  // Full confirm for the bulk delete (inline bar, shown on demand).
  const confirmBar = document.createElement('div');
  confirmBar.className = 'history-confirm';
  confirmBar.hidden = true;
  deleteAll.addEventListener('click', () => {
    deleteAll.disabled = true;
    confirmBar.hidden = false;
  });

  const listWrap = document.createElement('div');
  listWrap.className = 'history-dialog-list';

  const renderList = (): void => {
    const sessions = listSessions();
    listWrap.replaceChildren();
    confirmBar.hidden = true;
    deleteAll.disabled = sessions.length === 0;
    exportAll.disabled = sessions.length === 0;
    if (sessions.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'history-empty';
      empty.textContent = t('historyEmpty');
      listWrap.appendChild(empty);
      return;
    }
    const ul = document.createElement('ul');
    ul.className = 'history-list';
    for (const s of sessions) {
      try {
        ul.appendChild(buildModalItem(s));
      } catch (err) {
        console.warn('history: skipping unrenderable session', s.id, err);
      }
    }
    listWrap.appendChild(ul);
  };

  buildConfirmBar(confirmBar, () => {
    deleteAll.disabled = false;
  });

  dialog.append(header, tools, confirmBar, listWrap);
  document.body.appendChild(dialog);

  // Repaint the modal list on any change (own deletes, or a cloud import landing).
  const off = subscribeSessionsChanged(renderList);
  // Backdrop click closes (Escape is handled natively by <dialog>).
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) dialog.close();
  });
  dialog.addEventListener(
    'close',
    () => {
      off();
      dialog.remove();
    },
    { once: true },
  );

  renderList();
  dialog.showModal();
}

/** Wire the bulk-delete confirm bar (message + Confirm/Cancel). */
function buildConfirmBar(bar: HTMLElement, reArm: () => void): void {
  const msg = document.createElement('div');
  msg.className = 'history-confirm-msg';
  msg.textContent = t('confirmDeleteSessions');
  const row = document.createElement('div');
  row.className = 'history-confirm-row';
  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.className = 'history-del-btn';
  cancel.textContent = t('cancel');
  cancel.addEventListener('click', () => {
    bar.hidden = true;
    reArm();
  });
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'history-del-btn danger';
  confirm.textContent = t('confirmButton');
  confirm.addEventListener('click', () => {
    confirm.disabled = true;
    cancel.disabled = true;
    void performDeleteAll().finally(() => {
      confirm.disabled = false;
      cancel.disabled = false;
    });
  });
  row.append(cancel, confirm);
  bar.append(msg, row);
}

/** Expandable list item for the modal: summary + detail + export + delete. */
function buildModalItem(s: StoredSession): HTMLElement {
  const li = document.createElement('li');
  const item = document.createElement('details');
  item.className = 'history-item';

  const summary = document.createElement('summary');
  const date = document.createElement('span');
  date.className = 'hi-date';
  date.textContent = formatDate(s.startedAt);
  const type = document.createElement('span');
  type.className = 'hi-type';
  type.textContent = typeLabel(s.stats);
  const headline = document.createElement('span');
  headline.className = 'hi-headline';
  headline.textContent = headlineStat(s.stats);
  summary.append(date, type, headline);
  item.appendChild(summary);

  item.appendChild(buildDetail(s));
  li.appendChild(item);
  return li;
}

function buildDetail(s: StoredSession): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'history-detail';

  const grid = document.createElement('div');
  grid.className = 'history-metrics';
  for (const [label, value] of metricsFor(s)) grid.appendChild(metricRow(label, value));
  wrap.appendChild(grid);

  const actions = document.createElement('div');
  actions.className = 'history-detail-actions';

  const exportOne = document.createElement('button');
  exportOne.type = 'button';
  exportOne.className = 'history-export-one';
  exportOne.textContent = t('historyExportOne');
  exportOne.addEventListener('click', () => download(`session-${s.id}.json`, exportSessionJSON(s)));
  actions.appendChild(exportOne);
  actions.appendChild(buildDeleteControl(s.id));

  wrap.appendChild(actions);
  return wrap;
}

/** Inline two-step delete: button → Confirm/Cancel in place. */
function buildDeleteControl(id: string): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'history-delete';

  const del = document.createElement('button');
  del.type = 'button';
  del.className = 'history-del-btn danger';
  del.textContent = t('historyDeleteOne');
  del.addEventListener('click', () => {
    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'history-del-btn danger';
    confirm.textContent = t('confirmButton');
    confirm.addEventListener('click', () => {
      confirm.disabled = true;
      void performDelete(id);
    });
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'history-del-btn';
    cancel.textContent = t('cancel');
    cancel.addEventListener('click', () => wrap.replaceChildren(del));
    wrap.replaceChildren(confirm, cancel);
  });

  wrap.appendChild(del);
  return wrap;
}

/** Delete one session everywhere (cloud via lazy import when configured). */
async function performDelete(id: string): Promise<void> {
  if (isFirebaseConfigured()) {
    const { deleteSession: deleteEverywhere } = await import('../firebase/account');
    await deleteEverywhere(id);
  } else {
    deleteSession(id);
  }
}

/** Delete all sessions everywhere (cloud via lazy import when configured). */
async function performDeleteAll(): Promise<void> {
  if (isFirebaseConfigured()) {
    const { deleteAllSessions } = await import('../firebase/account');
    await deleteAllSessions();
  } else {
    clearAllSessions();
  }
}

// ── Trends ───────────────────────────────────────────────────
/** Edge-good % (pell sessions) and drill accuracy % sparklines, oldest→newest. */
function buildTrends(sessions: readonly StoredSession[]): HTMLElement | null {
  const chrono = [...sessions].reverse();
  const edge = chrono
    .filter((s) => s.stats.kind === 'pell')
    .map((s) => (s.stats as PellStats).edgeGoodPct);
  const acc = chrono
    .filter((s) => s.stats.kind === 'drill')
    .map((s) => (s.stats as DrillStats).accuracyPct);

  const rows: HTMLElement[] = [];
  if (edge.length >= 2) rows.push(trendRow(t('summaryEdgeGood'), edge));
  if (acc.length >= 2) rows.push(trendRow(t('summaryDrillAccuracy'), acc));
  if (rows.length === 0) return null;

  const wrap = document.createElement('div');
  wrap.className = 'history-trends';
  const title = document.createElement('div');
  title.className = 'history-trends-title';
  title.textContent = t('historyTrends');
  wrap.appendChild(title);
  for (const r of rows) wrap.appendChild(r);
  return wrap;
}

function trendRow(label: string, values: number[]): HTMLElement {
  const row = document.createElement('div');
  row.className = 'trend';
  const lbl = document.createElement('span');
  lbl.className = 'trend-label';
  lbl.textContent = label;
  const val = document.createElement('span');
  val.className = 'trend-value';
  val.textContent = pct(values[values.length - 1]!);
  row.append(lbl, sparkline(values), val);
  return row;
}

/** Tiny inline SVG sparkline for a 0..100 series. */
function sparkline(values: number[]): SVGSVGElement {
  const w = 120;
  const h = 28;
  const pad = 2;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  svg.setAttribute('class', 'sparkline');
  svg.setAttribute('width', String(w));
  svg.setAttribute('height', String(h));
  const stepX = values.length > 1 ? (w - 2 * pad) / (values.length - 1) : 0;
  const y = (v: number): number => h - pad - (Math.max(0, Math.min(100, v)) / 100) * (h - 2 * pad);
  const pts = values.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  const line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
  line.setAttribute('points', pts);
  line.setAttribute('fill', 'none');
  line.setAttribute('stroke', '#b94a2a');
  line.setAttribute('stroke-width', '1.5');
  line.setAttribute('stroke-linejoin', 'round');
  line.setAttribute('stroke-linecap', 'round');
  svg.appendChild(line);
  return svg;
}

// ── Metrics + helpers ────────────────────────────────────────
/** Metric (label, value) pairs per session kind — same numbers as the VR summary. */
function metricsFor(s: StoredSession): Array<[string, string]> {
  const stats = s.stats;
  const duration: [string, string] = [t('historyDuration'), formatClock(s.durationMs)];
  switch (stats.kind) {
    case 'pell':
      return [
        [t('summaryTotal'), String(stats.total)],
        [t('summaryThrusts'), String(stats.thrusts)],
        [t('summaryEdgeGood'), pct(stats.edgeGoodPct)],
        [t('filoOk'), pct(stats.edgeAcceptablePct)],
        [t('summaryAvgVelocity'), speed(stats.avgTipSpeed)],
        duration,
        ...pellExtraRows(stats),
      ];
    case 'targets':
      return [
        [t('summaryTotal'), String(stats.total)],
        [t('summaryHitRate'), pct(stats.hitRatePct)],
        [t('summaryAvgReaction'), seconds(stats.avgReactionMs)],
        [t('summaryAvgAccuracy'), cm(stats.avgAccuracyM)],
        [t('summaryAvgVelocity'), speed(stats.avgTipSpeed)],
        duration,
        [t('summaryZoneHitRate'), zoneRates(stats.hitRateByZone)],
      ];
    case 'drill':
      return [
        [t('drillTargetLabel'), drillTargetName(stats.target)],
        [t('summaryDrillCorrect'), `${stats.correct}/${stats.attempts}`],
        [t('summaryDrillAccuracy'), pct(stats.accuracyPct)],
        [t('summaryDrillAvgEdge'), deg(stats.avgEdgeDeg)],
        [t('summaryDrillTime'), formatClock(stats.elapsedMs)],
        ...drillReasonRows(stats),
      ];
    case 'arcade': {
      // `difficulty` is absent on sessions stored before #43 — show the row only when present.
      const diff = (stats as { difficulty?: Difficulty }).difficulty;
      const rows: Array<[string, string]> = [
        ...(diff ? [[t('summaryDifficulty'), difficultyLabel(diff)] as [string, string]] : []),
        [t('summaryScore'), String(stats.score)],
        [t('summaryMaxCombo'), String(stats.maxCombo)],
        [t('summaryHitRate'), `${pct(stats.accuracyPct)} (${stats.hits}/${stats.total})`],
      ];
      // Thrust runs carry the targets diagnostics (centering + reaction + per-zone hit rate);
      // cut runs (incl. legacy sessions with no `technique`) carry the edge-alignment angle.
      if (stats.technique === 'thrust') {
        rows.push([t('summaryAvgAccuracy'), cm(stats.avgAccuracyM)]);
        rows.push([t('summaryAvgReaction'), seconds(stats.avgReactionMs)]);
        rows.push(duration);
        rows.push([t('summaryZoneHitRate'), zoneRates(stats.hitRateByZone)]);
      } else {
        rows.push([t('summaryDrillAvgEdge'), deg(stats.avgEdgeDeg)]);
        rows.push(duration);
      }
      return rows;
    }
  }
}

function pellExtraRows(stats: PellStats): Array<[string, string]> {
  const rows: Array<[string, string]> = [];
  const sb = stats.strokeBreakdown;
  if (sb.classified > 0) {
    rows.push([
      t('strokeLato'),
      `${t('latoMandritto')} ${sb.lato.mandritto} · ${t('latoRiverso')} ${sb.lato.riverso}`,
    ]);
  }
  rows.push([zoneTitle(), zoneCounts(stats.zoneDistribution)]);
  return rows;
}

function drillReasonRows(stats: DrillStats): Array<[string, string]> {
  const labels: Record<DrillReasonKey, string> = {
    zona: t('reasonZona'),
    lato: t('strokeLato'),
    inclinazione: t('strokeInclinazione'),
    filo: t('strokeFilo'),
    piattonata: t('filoFlat'),
    ambiguo: t('reasonAmbiguo'),
  };
  const faults = (Object.keys(labels) as DrillReasonKey[])
    .filter((k) => stats.reasonCounts[k] > 0)
    .map((k) => `${labels[k]} ${stats.reasonCounts[k]}`);
  return faults.length > 0 ? [[t('summaryDrillReasons'), faults.join(' · ')]] : [];
}

function metricRow(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'history-metric';
  const l = document.createElement('span');
  l.textContent = label;
  const v = document.createElement('span');
  v.textContent = value;
  row.append(l, v);
  return row;
}

function typeLabel(stats: SessionStats): string {
  switch (stats.kind) {
    case 'pell':
      return t('drillTypePell');
    case 'targets':
      return t('drillTypeTargets');
    case 'drill':
      return t('drillTypePrescriptive');
    case 'arcade': {
      const base = stats.technique === 'thrust' ? t('drillTypeArcadeThrust') : t('drillTypeArcade');
      const diff = (stats as { difficulty?: Difficulty }).difficulty;
      return diff ? `${base} · ${difficultyLabel(diff)}` : base;
    }
  }
}

function headlineStat(stats: SessionStats): string {
  switch (stats.kind) {
    case 'pell':
      return `${t('summaryEdgeGood')} ${pct(stats.edgeGoodPct)}`;
    case 'targets':
      return `${t('summaryHitRate')} ${pct(stats.hitRatePct)}`;
    case 'drill':
      return `${t('summaryDrillAccuracy')} ${pct(stats.accuracyPct)}`;
    case 'arcade':
      return `${t('summaryScore')} ${stats.score}`;
  }
}

function zoneCounts(dist: Record<Zone, number>): string {
  return zoneOrder()
    .map((z) => `${zoneLabel(z)} ${dist[z]}`)
    .join(' · ');
}

function zoneRates(hitRateByZone: Record<Zone, number>): string {
  return zoneOrder()
    .map((z) => `${zoneLabel(z)} ${pct(hitRateByZone[z])}`)
    .join(' · ');
}

function zoneOrder(): Zone[] {
  return [ZONE.head, ZONE.shoulder, ZONE.chest, ZONE.flank, ZONE.leg];
}

/** Title for the per-zone breakdown line; reuses the d'arte "zones" wording. */
function zoneTitle(): string {
  return t('summaryZoneHitRate');
}

function formatDate(ms: number): string {
  try {
    return new Intl.DateTimeFormat(getLocale(), { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(ms),
    );
  } catch {
    return new Date(ms).toISOString();
  }
}

const pct = (v: number): string => `${Math.round(v)}%`;
const speed = (v: number): string => `${v.toFixed(1)} m/s`;
const seconds = (ms: number): string => `${(ms / 1000).toFixed(2)} s`;
const cm = (m: number): string => `${(m * 100).toFixed(1)} cm`;
const deg = (v: number): string => `${v.toFixed(1)}°`;

/** Trigger a client-side download of `text` as a file named `name`. */
function download(name: string, text: string): void {
  const blob = new Blob([text], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
